import React, { useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { getEntityDateColumn } from "../../utils/entityDateColumn";

const CustodianTable = ({
  custodians,
  onAdd,
  onEdit,
  onDelete,
  searchTerm,
  onSearchChange,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
}) => {
  const theme = useTheme();
  const columns = [
    {
      field: "name",
      headerName: "Full Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "abbreviation",
      headerName: "Abbreviation",
      flex: 1,
      minWidth: 150,
    },
    getEntityDateColumn({
      field: "date",
      headerName: "Creation Date",
      flex: 1,
      minWidth: 150,
    }),
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit(params.row)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.light + "20",
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => {
                onDelete(params.row);
              }}
              sx={{
                color: theme.palette.error.main,
                "&:hover": {
                  backgroundColor: theme.palette.error.light + "20",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    if (!searchTerm) return custodians;
    
    return custodians.filter((custodian) =>
      custodian.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      custodian.abbreviation?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [custodians, searchTerm]);

  // Add id field to each row for DataGrid
  const rowsWithId = filteredData.map((custodian) => ({
    ...custodian,
    id: custodian.custodianId || Math.random().toString(),
  }));

  const tableBg =
    theme.palette.mode === "dark"
      ? theme.palette.grey[100]
      : theme.palette.background.paper;

  return (
    <Box sx={{  width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search custodians..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Add Custodian
        </Button>
      </Box>

      <DataGrid
        rows={rowsWithId}
        columns={columns}
        paginationModel={{
          page: page || 0,
          pageSize: Math.min(pageSize || 1000, 1000),
        }}
        onPaginationModelChange={(model) => {
          onPageChange?.(model.page);
          onPageSizeChange?.(Math.min(model.pageSize, 1000));
        }}
        pageSizeOptions={[100, 200, 1000]}
        pagination
        density="compact"
        disableRowSelectionOnClick
        sx={{
          border: "none",
          height: "calc(100vh - 20px)",
          backgroundColor: tableBg,
          "& .MuiDataGrid-main": {
            backgroundColor: "inherit",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontWeight: 500,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          "& .MuiDataGrid-row": {
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
          },
          "& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller": {
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.grey[100],
            borderTop: `1px solid ${theme.palette.divider}`,
          },
        }}
      />
    </Box>
  );
};

export default CustodianTable;
